import './ContentEditableAreaForContextAttributes.scss';

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { FlightButton, FlightTooltip, getIcon } from '@flybits/design-system';
import AttributeMappingModal from 'components/FlowBuilder/FlowSidePanels/shared/AttributeMappingModal/AttributeMappingModal';
import { TAttributeParameter } from 'components/FlowBuilder/FlowSidePanels/types';
import usePlugins, { TContextAttribute, TContextPlugin } from 'hooks/useContextPlugins';
import { splitCtxString } from 'helpers/rule.helper';
import { CTX_ATTR_STRING_PATTERN } from 'constants/contextPlugins';

interface ContentEditableAreaForContextAttributesProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  width?: string;
  maxLength?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  hideCharCounter?: boolean;
  className?: string;
}

const MAIN_CLASS = 'content-editable-area-for-context-attributes';
const CLASSES = {
  MAIN: MAIN_CLASS,
  LABEL: `${MAIN_CLASS}__label`,
  LABEL_WRAPPER: `${MAIN_CLASS}__label-wrapper`,
  ADD_ATTRIBUTE_BUTTON: `${MAIN_CLASS}__add-attribute-button`,
  CONTAINER: `${MAIN_CLASS}__container`,
  CTX_ATTRIBUTE: `${MAIN_CLASS}__ctx-attribute`,
  CTX_ATTRIBUTE_ERROR: `${MAIN_CLASS}__ctx-attribute--error`,
  CTX_ATTRIBUTE_ERROR_ICON: `${MAIN_CLASS}__ctx-attribute__error-icon`,
  CTX_ATTRIBUTE_SELECTED: `${MAIN_CLASS}__ctx-attribute--selected`,
  ERROR_MESSAGE: `${MAIN_CLASS}__error-message`,
  COUNTER: `${MAIN_CLASS}__counter`,
  COUNTER_TOOLTIP: `${MAIN_CLASS}__counter-tooltip`,
  FOOTER: `${MAIN_CLASS}__footer`,
};

/**
 * Calculates the character length of a string containing HTML, excluding any text
 * within '{{...}}' placeholders. It achieves this by rendering the HTML in a temporary,
 * non-visible element and then using its textContent to count characters.
 * @param text The HTML string to measure.
 * @returns The character count, excluding placeholder content.
 */
const getLengthWithoutBraces = (text: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  return (tempDiv.textContent || tempDiv.innerText || '').replace(/{{(.*?)}}/g, '').length;
};

/**
 * Converts the mixed content (text nodes and attribute spans) of the contentEditable
 * div back into a plain text string. Attribute spans are converted back to '{{ctxAttrString}}' format.
 * @param element The contentEditable div element.
 * @returns A plain text string representation of the editor's content.
 */
const getPlainText = (element: HTMLDivElement) => {
  let plainText = '';
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      plainText += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset.ctxAttrString) {
        plainText += `{{${el.dataset.ctxAttrString}}}`;
      } else {
        plainText += el.textContent;
      }
    }
  });
  return plainText;
};

/**
 * Converts a selection range to plain text, including attribute placeholders.
 * This is used for copy operations to ensure attributes are copied as text.
 * @param container The root node to traverse from (the contentEditable div).
 * @param range The selection range to convert to plain text.
 * @returns A plain text string representation of the selected content.
 */
const getPlainTextFromRange = (container: Node, range: Range): string => {
  // Clone the contents of the range (doesn't modify the DOM, handles all edge cases)
  const contents = range.cloneContents();

  // Convert the cloned contents to plain text, replacing attribute spans with their text representation
  let text = '';

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset.ctxAttrString) {
        // Convert attribute span to its text representation
        text += `{{${el.dataset.ctxAttrString}}}`;
        // Don't process children - the attribute span's textContent is just the display text
      } else {
        // Process child nodes recursively
        for (let i = 0; i < node.childNodes.length; i++) {
          processNode(node.childNodes[i]);
        }
      }
    }
  };

  // Process all nodes in the cloned contents
  for (let i = 0; i < contents.childNodes.length; i++) {
    processNode(contents.childNodes[i]);
  }

  return text;
};

/**
 * Traverses the DOM of the contentEditable container to construct a plain text string
 * of the content from the beginning up to the start of a given Range (the cursor position).
 * This is used to accurately insert new attributes at the caret.
 * @param container The root node to traverse from (the contentEditable div).
 * @param range The selection range indicating the cursor position.
 * @returns A plain text string of the content before the cursor.
 */
const getPlainTextBeforeRange = (container: Node, range: Range): string => {
  let text = '';
  let ended = false;

  function recurse(node: Node) {
    if (ended) return;

    if (node === range.startContainer) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent?.substring(0, range.startOffset) ?? '';
      }
      ended = true;
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset && el.dataset.ctxAttrString) {
        text += `{{${el.dataset.ctxAttrString}}}`;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          recurse(node.childNodes[i]);
          if (ended) return;
        }
      }
    }
  }

  recurse(container);
  return text;
};

/**
 * Finds the specific attribute span element that contains or is at the given Range position.
 * This is used to identify which specific instance of an attribute is selected when there are
 * multiple identical attributes in the same field.
 * @param container The root node to search from (the contentEditable div).
 * @param range The selection range indicating the cursor/selection position.
 * @returns The attribute span element if found, or null if no attribute is at that position.
 */
const findAttributeSpanAtRange = (container: Node, range: Range): HTMLElement | null => {
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;

  // Helper to check if a node is an attribute span
  const isAttributeSpan = (node: Node): node is HTMLElement => {
    return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).dataset?.ctxAttrString !== undefined;
  };

  // Check if the start container is directly an attribute span
  if (isAttributeSpan(startContainer)) {
    return startContainer as HTMLElement;
  }

  // Check if the start container is inside an attribute span
  let node: Node | null = startContainer;
  while (node && node !== container) {
    if (isAttributeSpan(node)) {
      return node as HTMLElement;
    }
    node = node.parentNode;
  }

  // Check if the end container is directly an attribute span (for selections)
  if (isAttributeSpan(endContainer)) {
    return endContainer as HTMLElement;
  }

  // Check if the end container is inside an attribute span
  node = endContainer;
  while (node && node !== container) {
    if (isAttributeSpan(node)) {
      return node as HTMLElement;
    }
    node = node.parentNode;
  }

  // Check if the range spans across an attribute (selection includes an attribute)
  if (!range.collapsed) {
    const commonAncestor = range.commonAncestorContainer;
    if (commonAncestor && container.contains(commonAncestor)) {
      // Check if any child of commonAncestor is an attribute span
      const walker = document.createTreeWalker(commonAncestor, NodeFilter.SHOW_ELEMENT, {
        acceptNode: (node) => {
          if (isAttributeSpan(node)) {
            // Check if this attribute is within the range
            const rangeStart = range.startContainer;
            const rangeEnd = range.endContainer;
            let nodeToCheck: Node | null = node;
            while (nodeToCheck) {
              if (nodeToCheck === rangeStart || nodeToCheck === rangeEnd) {
                return NodeFilter.FILTER_ACCEPT;
              }
              if (nodeToCheck.contains(rangeStart) || nodeToCheck.contains(rangeEnd)) {
                return NodeFilter.FILTER_ACCEPT;
              }
              nodeToCheck = nodeToCheck.parentNode;
            }
          }
          return NodeFilter.FILTER_SKIP;
        },
      });

      const attributeNode = walker.nextNode();
      if (attributeNode && isAttributeSpan(attributeNode)) {
        return attributeNode as HTMLElement;
      }
    }
  }

  return null;
};

/**
 * Removes a specific occurrence of an attribute from a plain text string at a given position.
 * This ensures that when there are multiple identical attributes, only the one at the specified
 * position is removed, not the first occurrence.
 * @param text The plain text string containing attributes.
 * @param ctxAttrString The context attribute string (without the {{ }} braces).
 * @param position The position in the plain text string where the attribute should be removed.
 * @returns An object with the new text and the position where the attribute was removed.
 */
const removeAttributeAtPosition = (
  text: string,
  ctxAttrString: string,
  position: number,
): { newText: string; removalPosition: number } => {
  const attributePattern = `{{${ctxAttrString}}}`;
  const attributeLength = attributePattern.length;

  // Find all occurrences of the attribute
  const regex = new RegExp(`\\{\\{${ctxAttrString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`, 'g');
  const matches: Array<{ index: number; length: number }> = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    matches.push({ index: match.index, length: match[0].length });
  }

  // Find the occurrence that contains or is closest to the position
  let targetIndex = -1;

  // First, check if position is within any attribute
  for (let i = 0; i < matches.length; i++) {
    const matchStart = matches[i].index;
    const matchEnd = matchStart + matches[i].length;
    // If position is within this attribute (including at the start or end)
    if (position >= matchStart && position <= matchEnd) {
      targetIndex = matchStart;
      break;
    }
  }

  // If position is not within an attribute, find the one closest to the cursor
  // When an attribute is selected via backspace/delete, it's the one immediately before/after the cursor
  if (targetIndex === -1) {
    // Find the attribute that ends immediately before or at the cursor position (for backspace)
    let closestBeforeEnd = -1;
    let closestBeforeIndex = -1;

    // Also find the attribute that starts immediately after the cursor (for delete)
    let closestAfterStart = Infinity;
    let closestAfterIndex = -1;

    for (let i = 0; i < matches.length; i++) {
      const matchStart = matches[i].index;
      const matchEnd = matchStart + matches[i].length;

      // Attribute ends at or before the cursor position
      if (matchEnd <= position) {
        if (matchEnd > closestBeforeEnd) {
          closestBeforeEnd = matchEnd;
          closestBeforeIndex = matchStart;
        }
      }

      // Attribute starts at or after the cursor position
      if (matchStart >= position) {
        if (matchStart < closestAfterStart) {
          closestAfterStart = matchStart;
          closestAfterIndex = matchStart;
        }
      }
    }

    // Prefer the attribute immediately before the cursor (most common case for backspace)
    // This handles the case where cursor is right after an attribute: "text {{attr}}[cursor]"
    if (closestBeforeIndex !== -1) {
      targetIndex = closestBeforeIndex;
    } else if (closestAfterIndex !== -1) {
      // Fallback: attribute immediately after cursor (for delete key)
      targetIndex = closestAfterIndex;
    }
  }

  // If no match found, fall back to first occurrence (backward compatibility)
  if (targetIndex === -1 && matches.length > 0) {
    targetIndex = matches[0].index;
  }

  if (targetIndex !== -1) {
    const newText = text.substring(0, targetIndex) + text.substring(targetIndex + attributeLength);
    return { newText, removalPosition: targetIndex };
  }

  // Fallback: use replace for first occurrence (shouldn't happen in normal flow)
  const newText = text.replace(attributePattern, '');
  return { newText, removalPosition: text.indexOf(attributePattern) };
};

const validateAttribute = (ctxAttrString: string, allAttributes: TContextAttribute[]) => {
  let error = '';
  let attributeProps;

  try {
    const { errorMessage, ...rest } = splitCtxString(ctxAttrString, allAttributes);
    error = errorMessage;
    attributeProps = rest;
  } catch (e) {
    if (e instanceof Error) error = e.message;
    else error = 'Action needed: Unknown error with context attribute';
  }

  return [error, attributeProps] as const;
};

const ContentEditableAreaForContextAttributes: React.FC<ContentEditableAreaForContextAttributesProps> = ({
  label,
  value,
  onChange,
  hasError,
  errorMessage,
  width,
  maxLength,
  onFocus,
  onBlur,
  hideCharCounter = false,
  className = '',
}) => {
  const { plugins }: { plugins: TContextPlugin[] } = usePlugins();
  const allAttributes = useMemo(() => plugins.flatMap((plugin) => Object.values(plugin.values)), [plugins]);
  const editorRef = useRef<HTMLDivElement>(null);
  const [selectedCtxAttrString, setSelectedCtxAttrString] = useState('');
  const [selection, setSelection] = useState<Range | null>(null);
  const [caretPosition, setCaretPosition] = useState<number | null>(null);

  const [attributeModalState, setAttributeModalState] = useState({
    isVisible: false,
    title: '',
    tagName: '',
    primaryButtonLabel: '',
    showRemoveAttributeButton: false,
    attributeId: '',
    attributeMapKey: '',
    attributeParameters: [] as TAttributeParameter[],
    attributeDefaultValue: '',
  });

  /**
   * Parses the plain text `value` prop (which may contain '{{...}}' placeholders)
   * and converts it into an array of Text nodes and styled `<span>` elements for attributes.
   * This is used to render the content within the contentEditable div. It also applies
   * special classes for selected attributes or attributes with errors.
   */
  const parseValueToHTML = useCallback(
    (text: string) => {
      const regexSplit = new RegExp(`(\\{\\{${CTX_ATTR_STRING_PATTERN}+}})`, 'g');
      const regexExact = new RegExp(`^\\{\\{${CTX_ATTR_STRING_PATTERN}+}}$`);
      return text
        .split(regexSplit)
        .filter((part) => part)
        .map((part, idx, arr) => {
          if (regexExact.test(part)) {
            const prev = arr[idx - 1] || '';
            const next = arr[idx + 1] || '';
            if (prev.endsWith('{') || next.startsWith('}')) {
              return document.createTextNode(part);
            }
            const ctxAttrString = part.substring(2, part.length - 2);
            // UI-level check for comma placement: only one comma, not at start/end, not in the middle of the attribute part
            let uiError = '';
            const commaIndex = ctxAttrString.indexOf(',');
            if (commaIndex > -1) {
              if (
                commaIndex === 0 ||
                commaIndex === ctxAttrString.length - 1 ||
                ctxAttrString.indexOf(',', commaIndex + 1) !== -1 ||
                ctxAttrString.slice(0, commaIndex).includes(',')
              ) {
                uiError = 'Invalid: Only one comma allowed, just before default value';
              }
            }
            const [errorMessage] = validateAttribute(ctxAttrString, allAttributes);
            const isError = !!errorMessage || !!uiError;
            // Don't mark as selected here - the useEffect will handle highlighting only the correct instance
            const span = document.createElement('span');
            span.className = `${CLASSES.CTX_ATTRIBUTE} ${isError ? CLASSES.CTX_ATTRIBUTE_ERROR : ''}`;
            span.dataset.ctxAttrString = ctxAttrString;
            span.contentEditable = 'false';
            span.title = uiError ? uiError : isError ? errorMessage : 'Edit this attribute';
            span.textContent = part;
            return span;
          }
          return document.createTextNode(part);
        });
    },
    [allAttributes],
  );

  const openEditModal = (ctxAttrString: string) => {
    const [, attributeProps] = validateAttribute(ctxAttrString, allAttributes);

    setAttributeModalState((state) => ({
      ...state,
      isVisible: true,
      title: 'Edit dynamic attribute',
      primaryButtonLabel: 'Update attribute',
      showRemoveAttributeButton: true,
      attributeId: attributeProps?.attributeId || '',
      attributeMapKey: attributeProps?.attributeMapKey || '',
      attributeParameters: attributeProps?.attributeParameters || [],
      attributeDefaultValue: attributeProps?.defaultValue || '',
    }));
  };

  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const selection = window.getSelection();

    if (target.dataset.ctxAttrString) {
      const ctxAttrString = target.dataset.ctxAttrString;

      // Always create a range that selects the entire attribute span to track which specific attribute was clicked
      const range = document.createRange();
      range.selectNodeContents(target);
      setSelection(range);
      // Also update the browser selection to visually select the attribute
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }

      // If the attribute is already selected, open the modal for editing.
      if (selectedCtxAttrString === ctxAttrString) {
        openEditModal(ctxAttrString);
      } else {
        // Otherwise, just select the attribute.
        setSelectedCtxAttrString(ctxAttrString);
      }
    } else {
      // Deselect an attribute when the user clicks away from it
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        setSelection(range.cloneRange());
        const node = range.startContainer;
        const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
        if (element?.dataset?.ctxAttrString) {
          // Clicked inside an attribute, update selection state
          setSelectedCtxAttrString(element.dataset.ctxAttrString);
        } else if (selectedCtxAttrString) {
          // Clicked outside of a selected attribute, so deselect it.
          setSelectedCtxAttrString('');
        }
      }
    }
  };

  const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editorRef.current) {
      return;
    }

    const range = selection.getRangeAt(0);

    // Check if the selection is within our editor
    if (!editorRef.current.contains(range.commonAncestorContainer)) {
      return;
    }

    // Convert the selected range to plain text (including attributes)
    const plainText = getPlainTextFromRange(editorRef.current, range);

    // Set the clipboard data
    e.clipboardData.setData('text/plain', plainText);
    e.preventDefault();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const node = range.startContainer;

    // Handles deleting an attribute that is already selected.
    // If 'Backspace' or 'Delete' is pressed while an attribute is selected (highlighted),
    // it prevents the default browser action, calls a function to remove the attribute, and stops.
    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedCtxAttrString) {
      e.preventDefault();
      // Ensure selection Range is updated before removal
      setSelection(range.cloneRange());
      handleRemoveAttribute();
      return;
    }
    // Handles the case where the caret is just before an attribute and Delete is pressed
    if (e.key === 'Delete' && !selectedCtxAttrString && range.collapsed) {
      // If caret is just before an attribute span, select it
      let nextNode = node.nodeType === Node.TEXT_NODE && node.nextSibling ? node.nextSibling : null;
      if (!nextNode && node.nodeType === Node.ELEMENT_NODE) {
        nextNode = node.childNodes[range.startOffset] || null;
      }
      if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE && (nextNode as HTMLElement).dataset?.ctxAttrString) {
        e.preventDefault();
        const ctxAttr = (nextNode as HTMLElement).dataset?.ctxAttrString || '';
        // Create a range that selects the entire attribute span
        const attrRange = document.createRange();
        attrRange.selectNodeContents(nextNode);
        setSelection(attrRange);
        // Update browser selection to visually select the attribute
        sel.removeAllRanges();
        sel.addRange(attrRange);
        setSelectedCtxAttrString(ctxAttr);
        // After selection, the next Delete will remove it (handled above)
      }
    }

    // Handles opening the edit modal for a selected attribute.
    // If the 'Enter' key is pressed while an attribute is selected,
    // it prevents default behavior (like creating a new line) and opens the attribute editing modal.
    if (e.key === 'Enter' && selectedCtxAttrString) {
      e.preventDefault();
      // Ensure selection Range is updated before opening modal
      setSelection(range.cloneRange());
      openEditModal(selectedCtxAttrString);
      return;
    }

    // Handles the "first backspace" action, which selects an attribute instead of deleting a character.
    // It only triggers if 'Backspace' is pressed and the cursor is not highlighting a selection (range.collapsed is true).
    if (e.key === 'Backspace' && range.collapsed) {
      const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;

      // Handles the case where the cursor is inside an attribute's text content.
      // If the cursor is at the very end of the attribute's text, it prevents character deletion
      // and instead selects the attribute.
      if (element?.dataset?.ctxAttrString) {
        const textContentLength = element.textContent?.length ?? 0;
        if (range.startOffset === textContentLength) {
          e.preventDefault();
          // Create a range that selects the entire attribute span
          const attrRange = document.createRange();
          attrRange.selectNodeContents(element);
          setSelection(attrRange);
          // Update browser selection to visually select the attribute
          sel.removeAllRanges();
          sel.addRange(attrRange);
          setSelectedCtxAttrString(element.dataset.ctxAttrString);
        }
        // Handles the case where the cursor is at the beginning of a text node that immediately follows an attribute.
        // Pressing backspace selects the preceding attribute.
      } else if (range.startOffset === 0 && node.previousSibling) {
        const prevSibling = node.previousSibling as HTMLElement;
        if (prevSibling?.dataset?.ctxAttrString) {
          e.preventDefault();
          // Create a range that selects the entire attribute span
          const attrRange = document.createRange();
          attrRange.selectNodeContents(prevSibling);
          setSelection(attrRange);
          // Update browser selection to visually select the attribute
          sel.removeAllRanges();
          sel.addRange(attrRange);
          setSelectedCtxAttrString(prevSibling.dataset.ctxAttrString);
        }
        // Handles the case where the cursor is at the end of the contenteditable area,
        // and the last item is an attribute. It checks the node just before the cursor's position
        // inside the main container and selects it if it's an attribute.
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).className.includes(CLASSES.CONTAINER) &&
        range.startOffset > 0
      ) {
        const child = node.childNodes[range.startOffset - 1] as HTMLElement;
        if (child?.dataset?.ctxAttrString) {
          e.preventDefault();
          // Create a range that selects the entire attribute span
          const attrRange = document.createRange();
          attrRange.selectNodeContents(child);
          setSelection(attrRange);
          // Update browser selection to visually select the attribute
          sel.removeAllRanges();
          sel.addRange(attrRange);
          setSelectedCtxAttrString(child.dataset.ctxAttrString);
        }
      }
    }
  };

  const handleAddAttribute = () => {
    setSelectedCtxAttrString('');
    setAttributeModalState({
      isVisible: true,
      title: 'Add dynamic attribute',
      tagName: '',
      primaryButtonLabel: 'Add attribute',
      showRemoveAttributeButton: false,
      attributeId: '',
      attributeMapKey: '',
      attributeParameters: [],
      attributeDefaultValue: '',
    });
  };

  const onClose = () => {
    setAttributeModalState({
      isVisible: false,
      title: '',
      tagName: '',
      primaryButtonLabel: '',
      showRemoveAttributeButton: false,
      attributeId: '',
      attributeMapKey: '',
      attributeParameters: [],
      attributeDefaultValue: '',
    });
    setSelectedCtxAttrString('');
  };

  const handleApplyAttributeMapping = (
    _tagName: string,
    attributeId: string,
    parameters: TAttributeParameter[],
    defaultValue: string,
    selectedKey?: string,
  ) => {
    const params = parameters?.map((p) => p.value).join('.');
    const newAttribute = `{{${attributeId}${selectedKey ? `.${selectedKey}` : ''}${params ? `.${params}` : ''}${
      defaultValue ? `,${defaultValue}` : ''
    }}}`;
    let newText;

    if (selectedCtxAttrString && editorRef.current) {
      // Replace existing attribute - find the specific selected attribute span
      const currentSelection = window.getSelection();
      let selectedAttributeSpan: HTMLElement | null = null;

      // Try to find the selected attribute span from current selection
      if (currentSelection && currentSelection.rangeCount > 0) {
        const range = currentSelection.getRangeAt(0);
        selectedAttributeSpan = findAttributeSpanAtRange(editorRef.current, range);
      }

      // If not found, try stored selection
      if (!selectedAttributeSpan && selection) {
        selectedAttributeSpan = findAttributeSpanAtRange(editorRef.current, selection);
      }

      // If still not found, find all matching attributes and check which one is selected
      if (!selectedAttributeSpan && editorRef.current) {
        const allAttributeSpans = Array.from(
          editorRef.current.querySelectorAll(`[data-ctx-attr-string]`),
        ) as HTMLElement[];
        const matchingSpans = allAttributeSpans.filter((span) => span.dataset.ctxAttrString === selectedCtxAttrString);

        // Find which span is selected by checking if it has the selected class or intersects with selection
        for (const span of matchingSpans) {
          if (span.classList.contains(CLASSES.CTX_ATTRIBUTE_SELECTED)) {
            selectedAttributeSpan = span;
            break;
          }
          if (currentSelection && currentSelection.rangeCount > 0) {
            const range = currentSelection.getRangeAt(0);
            if (range.intersectsNode(span) || span.contains(range.startContainer)) {
              selectedAttributeSpan = span;
              break;
            }
          }
        }
      }

      // If we found the selected attribute span, replace it at the correct position
      if (selectedAttributeSpan && selectedAttributeSpan.dataset.ctxAttrString === selectedCtxAttrString) {
        const rangeBeforeAttr = document.createRange();
        rangeBeforeAttr.setStartBefore(selectedAttributeSpan);
        rangeBeforeAttr.collapse(true);
        const replacementPosition = getPlainTextBeforeRange(editorRef.current, rangeBeforeAttr).length;

        // Replace the specific attribute at the calculated position
        const { newText: textAfterRemoval, removalPosition } = removeAttributeAtPosition(
          value,
          selectedCtxAttrString,
          replacementPosition,
        );

        // Insert the new attribute at the removal position
        newText =
          textAfterRemoval.substring(0, removalPosition) + newAttribute + textAfterRemoval.substring(removalPosition);
      } else {
        // Fallback: replace first occurrence (old behavior) - should not happen in normal flow
        newText = value.replace(`{{${selectedCtxAttrString}}}`, newAttribute);
      }
    } else {
      // Insert new attribute at caret position
      if (editorRef.current && selection) {
        const preCaretText = getPlainTextBeforeRange(editorRef.current, selection);
        const postCaretText = value.substring(preCaretText.length);

        newText = preCaretText + newAttribute + postCaretText;
      } else {
        newText = value + newAttribute;
      }
    }
    onChange(newText);
    onClose();
  };

  const handleRemoveAttribute = () => {
    if (selectedCtxAttrString && editorRef.current) {
      // Get the current cursor position to find which specific attribute instance to remove
      const currentSelection = window.getSelection();
      let cursorPosition: number | null = null;

      // Calculate cursor position in plain text
      if (currentSelection && currentSelection.rangeCount > 0) {
        const range = currentSelection.getRangeAt(0);
        cursorPosition = getPlainTextBeforeRange(editorRef.current, range).length;
      } else if (selection) {
        cursorPosition = getPlainTextBeforeRange(editorRef.current, selection).length;
      }

      // If we have a cursor position, use it to find and remove the closest attribute
      if (cursorPosition !== null) {
        // Remove the specific attribute at the cursor position (will find the one closest to cursor)
        const { newText, removalPosition: actualRemovalPosition } = removeAttributeAtPosition(
          value,
          selectedCtxAttrString,
          cursorPosition,
        );

        // Set caret position to where the attribute was removed
        setCaretPosition(actualRemovalPosition);
        setSelectedCtxAttrString('');
        onChange(newText);
      } else {
        // Fallback: try to find selected attribute span in DOM
        let selectedAttributeSpan: HTMLElement | null = null;

        if (currentSelection && currentSelection.rangeCount > 0) {
          const range = currentSelection.getRangeAt(0);
          selectedAttributeSpan = findAttributeSpanAtRange(editorRef.current, range);
        }

        if (!selectedAttributeSpan && selection) {
          selectedAttributeSpan = findAttributeSpanAtRange(editorRef.current, selection);
        }

        if (!selectedAttributeSpan && editorRef.current) {
          const allAttributeSpans = Array.from(
            editorRef.current.querySelectorAll(`[data-ctx-attr-string]`),
          ) as HTMLElement[];
          const matchingSpans = allAttributeSpans.filter(
            (span) => span.dataset.ctxAttrString === selectedCtxAttrString,
          );

          for (const span of matchingSpans) {
            if (span.classList.contains(CLASSES.CTX_ATTRIBUTE_SELECTED)) {
              selectedAttributeSpan = span;
              break;
            }
          }
        }

        if (selectedAttributeSpan && selectedAttributeSpan.dataset.ctxAttrString === selectedCtxAttrString) {
          const rangeBeforeAttr = document.createRange();
          rangeBeforeAttr.setStartBefore(selectedAttributeSpan);
          rangeBeforeAttr.collapse(true);
          const removalPosition = getPlainTextBeforeRange(editorRef.current, rangeBeforeAttr).length;

          const { newText, removalPosition: actualRemovalPosition } = removeAttributeAtPosition(
            value,
            selectedCtxAttrString,
            removalPosition,
          );

          setCaretPosition(actualRemovalPosition);
          setSelectedCtxAttrString('');
          onChange(newText);
        } else {
          // Last resort: remove first occurrence (old behavior)
          const newText = value.replace(`{{${selectedCtxAttrString}}}`, '');
          const attrIndex = value.indexOf(`{{${selectedCtxAttrString}}}`);
          setCaretPosition(attrIndex !== -1 ? attrIndex : newText.length);
          setSelectedCtxAttrString('');
          onChange(newText);
        }
      }
      onClose();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    const editor = editorRef.current;
    let caretPos: number | null = null;
    if (editor && sel?.rangeCount) {
      caretPos = getPlainTextBeforeRange(editor, sel.getRangeAt(0)).length;
    }
    const plain = getPlainText(e.currentTarget);
    onChange(plain);
    if (caretPos === null || !editor) return;
    const nodes = editor.childNodes;
    // Fix: If caret is at the end and the last node is a non-editable attribute span, place caret after the span
    if (caretPos === plain.length && nodes.length > 0) {
      const last = nodes[nodes.length - 1];
      if (last.nodeType === Node.ELEMENT_NODE && (last as HTMLElement).dataset.ctxAttrString) {
        setCaretPosition(plain.length);
        return;
      }
    }
    // If caret is after an attribute and a space, and user backspaced the space, keep caret after the attribute
    const before = plain.slice(0, caretPos);
    const regexEnd = new RegExp(`\\{\\{${CTX_ATTR_STRING_PATTERN}+}}$`);
    if (regexEnd.test(before)) {
      setCaretPosition(caretPos);
    }
  };

  /**
   * This effect synchronizes the DOM content of the editor with the `value` prop.
   * It compares the new nodes generated from `parseValueToHTML` with the existing
   * nodes in the editor and performs minimal updates (add, remove, replace) to
   * reflect the new state. This avoids a full re-render of the contentEditable div,
   * which would cause the user to lose their cursor position.
   */
  useEffect(() => {
    if (editorRef.current) {
      const newNodes = parseValueToHTML(value);
      const existingNodes = Array.from(editorRef.current.childNodes);

      let i = 0;
      let j = 0;
      while (i < newNodes.length || j < existingNodes.length) {
        const newNode = newNodes[i];
        const existingNode = existingNodes[j];

        if (!newNode) {
          // newNodes is shorter, remove remaining existing nodes
          editorRef.current.removeChild(existingNode);
          j++;
          continue;
        }

        if (!existingNode) {
          // newNodes is longer, add new nodes
          editorRef.current.appendChild(newNode);
          i++;
          continue;
        }

        if (newNode.nodeType !== existingNode.nodeType || newNode.textContent !== existingNode.textContent) {
          editorRef.current.replaceChild(newNode, existingNode);
        } else if (newNode.nodeType === Node.ELEMENT_NODE && existingNode.nodeType === Node.ELEMENT_NODE) {
          const newElement = newNode as HTMLElement;
          const existingElement = existingNode as HTMLElement;
          if (newElement.className !== existingElement.className) {
            existingElement.className = newElement.className;
          }
        }

        i++;
        j++;
      }
    }
  }, [value, parseValueToHTML]);

  /**
   * This effect ensures only the attribute that matches the current selection range is highlighted.
   * When multiple identical attributes exist, only the one nearest to the cursor should be selected.
   */
  useEffect(() => {
    if (editorRef.current && selectedCtxAttrString) {
      const currentSelection = window.getSelection();
      let targetRange: Range | null = null;

      // Get the current selection range
      if (currentSelection && currentSelection.rangeCount > 0) {
        targetRange = currentSelection.getRangeAt(0);
      } else if (selection) {
        targetRange = selection;
      }

      if (targetRange) {
        // Find which specific attribute span is selected
        const selectedAttributeSpan = findAttributeSpanAtRange(editorRef.current, targetRange);

        // Remove selected class from all attributes with this string
        const allAttributeSpans = Array.from(
          editorRef.current.querySelectorAll(`[data-ctx-attr-string="${selectedCtxAttrString.replace(/"/g, '\\"')}"]`),
        ) as HTMLElement[];

        allAttributeSpans.forEach((span) => {
          span.classList.remove(CLASSES.CTX_ATTRIBUTE_SELECTED);
          // Also remove the error-selected variant if present
          span.classList.remove(`${CLASSES.CTX_ATTRIBUTE_ERROR}--selected`);
        });

        // Add selected class only to the attribute that matches the selection range
        if (selectedAttributeSpan && selectedAttributeSpan.dataset.ctxAttrString === selectedCtxAttrString) {
          if (selectedAttributeSpan.classList.contains(CLASSES.CTX_ATTRIBUTE_ERROR)) {
            selectedAttributeSpan.classList.add(`${CLASSES.CTX_ATTRIBUTE_ERROR}--selected`);
          } else {
            selectedAttributeSpan.classList.add(CLASSES.CTX_ATTRIBUTE_SELECTED);
          }
        }
      }
    } else if (editorRef.current && !selectedCtxAttrString) {
      // If no attribute is selected, remove selected class from all attributes
      const allAttributeSpans = Array.from(
        editorRef.current.querySelectorAll(`[data-ctx-attr-string]`),
      ) as HTMLElement[];
      allAttributeSpans.forEach((span) => {
        span.classList.remove(CLASSES.CTX_ATTRIBUTE_SELECTED);
        span.classList.remove(`${CLASSES.CTX_ATTRIBUTE_ERROR}--selected`);
      });
    }
  }, [selectedCtxAttrString, selection, value]);

  /**
   * This effect programmatically restores the caret (cursor) position within the editor.
   * It runs whenever the `caretPosition` state is updated, which typically happens
   * after an operation (like deleting or typing an attribute) that causes a re-render and
   * would otherwise lead to the loss of the cursor's position.
   *
   * The effect works by traversing the child nodes of the editor, counting characters
   * to find the specific text node and offset that corresponds to the `caretPosition`
   * value. If the caret is inside or at the edge of an attribute span, it positions
   * the cursor immediately after that span. It then creates a new selection Range and
   * applies it to restore the caret. Finally, it resets `caretPosition` to `null` to
   * prevent re-running on subsequent renders.
   */
  useEffect(() => {
    if (editorRef.current && caretPosition !== null) {
      const sel = window.getSelection();
      if (sel) {
        let charCount = 0;
        let targetNode: Node | null = null;
        let targetOffset = 0;

        const findNodeAndOffset = (node: Node) => {
          if (targetNode) return;

          if (node.nodeType === Node.TEXT_NODE) {
            const textLength = node.textContent?.length ?? 0;
            if (charCount + textLength >= caretPosition) {
              targetNode = node;
              targetOffset = caretPosition - charCount;
            } else {
              charCount += textLength;
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.dataset.ctxAttrString) {
              const textLength = el.textContent?.length ?? 0;
              if (charCount + textLength >= caretPosition) {
                // Position cursor after the attribute span
                if (el.parentNode) {
                  targetNode = el.parentNode;
                  targetOffset = Array.from(targetNode.childNodes).indexOf(el) + 1;
                }
              } else {
                charCount += textLength;
              }
            } else {
              node.childNodes.forEach(findNodeAndOffset);
            }
          }
        };

        findNodeAndOffset(editorRef.current);

        if (targetNode) {
          const range = document.createRange();
          range.setStart(targetNode, targetOffset);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      setCaretPosition(null); // Reset after setting position
    }
  }, [caretPosition]);

  /**
   * This effect tracks the user's cursor position (selection) within the document.
   * It adds a 'selectionchange' event listener to update the component's `selection`
   * state whenever the user clicks or moves the caret. It also handles deselecting a
   * selected attribute if the user's selection moves outside of it.
   */
  useEffect(() => {
    const handleSelectionChange = () => {
      // Issue: Updating an attribute after selecting it with the backspace and enter keys would incorrectly insert the new attribute instead of replacing the existing one.
      // The problem was that the selectedAttributeId was being reset when the attribute editing modal opened.

      // Fix: Prevent the selection change handler from running while the modal is visible, which resolves the issue.
      if (attributeModalState.isVisible) {
        return;
      }

      const currentSelection = window.getSelection();
      if (
        currentSelection &&
        currentSelection.rangeCount > 0 &&
        editorRef.current?.contains(currentSelection.anchorNode)
      ) {
        const range = currentSelection.getRangeAt(0);

        if (
          !selection ||
          range.startContainer !== selection.startContainer ||
          range.startOffset !== selection.startOffset ||
          range.endContainer !== selection.endContainer ||
          range.endOffset !== selection.endOffset
        ) {
          setSelection(range.cloneRange());
        }

        if (selectedCtxAttrString) {
          const node = range.startContainer;
          const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
          if (element?.dataset?.ctxAttrString !== selectedCtxAttrString) {
            setSelectedCtxAttrString('');
          }
        }
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [selectedCtxAttrString, selection, attributeModalState.isVisible]);

  const {
    isVisible,
    title,
    tagName,
    primaryButtonLabel,
    showRemoveAttributeButton,
    attributeId,
    attributeMapKey,
    attributeParameters,
    attributeDefaultValue,
  } = attributeModalState;

  return (
    <>
      <div className={`${CLASSES.MAIN} ${className}`} style={{ width }}>
        <header className={CLASSES.LABEL_WRAPPER}>
          <label className={CLASSES.LABEL}>{label}</label>
          <div className={CLASSES.ADD_ATTRIBUTE_BUTTON}>
            <FlightButton
              ariaLabel={'add dynamic attribute'}
              label="{+} Add attribute"
              theme="link"
              onClick={handleAddAttribute}
            />
          </div>
        </header>
        <div
          ref={editorRef}
          className={CLASSES.CONTAINER}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onClick={handleEditorClick}
          onKeyDown={handleKeyDown}
          onCopy={handleCopy}
          role="textbox"
          tabIndex={0}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <div className={CLASSES.FOOTER}>
          {hasError && <span className={CLASSES.ERROR_MESSAGE}>{errorMessage}</span>}
          {!hideCharCounter && !!maxLength && (
            <>
              <span className={CLASSES.COUNTER}>
                {getLengthWithoutBraces(value)} / {maxLength}
              </span>
              {/{{(.*?)}}/.test(value) && (
                <span className={CLASSES.COUNTER_TOOLTIP}>
                  <FlightTooltip
                    description="This count does not include text from added attributes. The final message length can vary by user."
                    direction="left"
                  >
                    {getIcon('exclamationCircle', {})}
                  </FlightTooltip>
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <AttributeMappingModal
        isVisible={isVisible}
        onClose={onClose}
        onApply={handleApplyAttributeMapping}
        tagName={tagName}
        attributeId={attributeId}
        attributeMapKey={attributeMapKey}
        attributeParameters={attributeParameters}
        attributeDefaultValue={attributeDefaultValue}
        showRemoveAttributeButton={showRemoveAttributeButton}
        onRemoveAttribute={handleRemoveAttribute}
        title={title}
        primaryButtonLabel={primaryButtonLabel}
      />
    </>
  );
};

export default ContentEditableAreaForContextAttributes;
